import { Mnemonic, PrivateKey, PublicKey } from '@hashgraph/sdk';

// The free, on-device recovery phrase check (plan decision D4): the phrase is turned into its key pair here, and its
// public key compared with the one stored for the person. Nothing is sent anywhere.

/** The ED25519 private key a 24-word recovery phrase stands for. Throws on a phrase that isn't a valid one. */
export async function privateKeyFromPhrase(phrase: string): Promise<PrivateKey> {
  const mnemonic = await Mnemonic.fromString(phrase.trim().split(/\s+/).join(' '));
  return mnemonic.toEd25519PrivateKey();
}

/**
 * Whether two public keys are the same key, whatever their encoding (DER or raw hex). A missing or unreadable key matches
 * nothing.
 */
export function samePublicKey(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  try {
    return PublicKey.fromString(a).toStringRaw() === PublicKey.fromString(b).toStringRaw();
  } catch {
    return false;
  }
}

/** Whether a recovery phrase belongs to the stored public key. A phrase that isn't a valid one belongs to nothing. */
export async function phraseMatchesPublicKey(phrase: string, storedPublicKey: string | null | undefined) {
  if (!storedPublicKey) return false;
  try {
    const privateKey = await privateKeyFromPhrase(phrase);
    return samePublicKey(privateKey.publicKey.toStringDer(), storedPublicKey);
  } catch {
    return false;
  }
}

export type CreateAccountStart = 'NEW' | 'RETRY' | 'HAS_ACCOUNT' | 'KEY_ELSEWHERE';

/**
 * Whether the create-account card may call the server, from what the person's profile and this device hold:
 * - NEW: the profile has no account yet;
 * - RETRY: the profile's key is the one this device kept for an account creation that hasn't finished (a set-up that stopped
 *   part way, or whose answer never arrived), so trying again goes on with it;
 * - HAS_ACCOUNT: the profile already has an account, and this device holds its key (or the profile names no key);
 * - KEY_ELSEWHERE: the profile has an account whose key this device doesn't hold, with no unfinished creation here: it was
 *   made in another browser, or this browser's site data was cleared since.
 */
export function createAccountStart(input: {
  profilePublicKey: string | null | undefined;
  // Whether the profile holds any other account detail (account id, url, topic, phrase words, verification)
  profileHasAccountDetails: boolean;
  pendingPublicKey: string | null | undefined;
  devicePublicKey: string | null | undefined;
}): CreateAccountStart {
  const { profilePublicKey, profileHasAccountDetails, pendingPublicKey, devicePublicKey } = input;
  if (samePublicKey(profilePublicKey, pendingPublicKey)) return 'RETRY';
  if (!profilePublicKey && !profileHasAccountDetails) return 'NEW';
  if (!profilePublicKey || samePublicKey(profilePublicKey, devicePublicKey)) return 'HAS_ACCOUNT';
  return 'KEY_ELSEWHERE';
}

export type RecoveryCheck = 'LOCAL' | 'PAID';

/**
 * How a recovery phrase is checked on this device. When the device already holds the person's public key (as it does
 * straight after the account is made, in onboarding's second job), the check is the free local comparison. Only a device
 * without it uses the paid lookup, paid by the company's operating account (OQ13).
 */
export function recoveryCheckMode(devicePublicKey: string | null | undefined): RecoveryCheck {
  return devicePublicKey ? 'LOCAL' : 'PAID';
}
